import { useState, useEffect, useCallback } from 'react';
import type { AssetStatus } from '@/types/service';
import { prefixCssSelectors } from '@/utils/css-prefix';

interface UseStylesheetLoaderOptions {
  /** Maximum file size in bytes (default: 500KB) */
  maxSize?: number;
  /** Timeout in milliseconds (default: 5000) */
  timeout?: number;
  /** Debounce delay in milliseconds (default: 300) */
  debounce?: number;
}

interface UseStylesheetLoaderReturn {
  /** Current loading/validation status */
  status: AssetStatus;
  /** Prefixed CSS string ready for injection, or null */
  prefixedCss: string | null;
  /** Reset to empty state */
  reset: () => void;
}

const DEFAULT_MAX_SIZE = 500 * 1024; // 500 KB
const DEFAULT_TIMEOUT = 5000; // 5 seconds
const DEFAULT_DEBOUNCE = 300; // 300ms
const CSS_PREFIX = '.preview-container';

/**
 * Hook to load, sanitize, and prefix external CSS stylesheets for safe
 * injection into the payment page preview.
 *
 * Flow:
 * 1. Validate URL format (HTTPS required)
 * 2. HEAD request for size check (with fallback if HEAD fails)
 * 3. GET request to fetch CSS text
 * 4. Sanitize (strip @import, expression(), non-https url())
 * 5. Prefix all selectors with .preview-container
 * 6. Return prefixed CSS for <style> injection
 */
export function useStylesheetLoader(
  url: string | undefined,
  options: UseStylesheetLoaderOptions = {}
): UseStylesheetLoaderReturn {
  const {
    maxSize = DEFAULT_MAX_SIZE,
    timeout = DEFAULT_TIMEOUT,
    debounce = DEFAULT_DEBOUNCE,
  } = options;

  const [status, setStatus] = useState<AssetStatus>({ status: 'empty' });
  const [prefixedCss, setPrefixedCss] = useState<string | null>(null);

  const loadStylesheet = useCallback(async (urlToLoad: string) => {
    // Empty URL
    if (!urlToLoad || urlToLoad.trim() === '') {
      setStatus({ status: 'empty' });
      setPrefixedCss(null);
      return;
    }

    // URL format validation
    if (!urlToLoad.startsWith('https://')) {
      setStatus({ status: 'error', message: 'URL muss mit https:// beginnen' });
      setPrefixedCss(null);
      return;
    }

    // Start loading
    setStatus({ status: 'loading' });

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      // HEAD request for size check (with fallback)
      try {
        const headResponse = await fetch(urlToLoad, {
          method: 'HEAD',
          mode: 'cors',
          signal: controller.signal,
        });

        if (!headResponse.ok) {
          clearTimeout(timeoutId);
          setStatus({ status: 'error', message: `HTTP ${headResponse.status}` });
          setPrefixedCss(null);
          return;
        }

        const contentLength = headResponse.headers.get('content-length');
        if (contentLength) {
          const size = parseInt(contentLength, 10);
          if (size > maxSize) {
            clearTimeout(timeoutId);
            const sizeKB = Math.round(size / 1024);
            const maxKB = Math.round(maxSize / 1024);
            setStatus({
              status: 'error',
              message: `Datei zu groß (${sizeKB} KB > ${maxKB} KB)`,
            });
            setPrefixedCss(null);
            return;
          }
        }
      } catch {
        // HEAD failed (CORS or not supported) - continue with GET
      }

      // GET request to fetch actual CSS
      const getResponse = await fetch(urlToLoad, {
        method: 'GET',
        mode: 'cors',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!getResponse.ok) {
        setStatus({ status: 'error', message: `HTTP ${getResponse.status}` });
        setPrefixedCss(null);
        return;
      }

      const cssText = await getResponse.text();

      // Check actual size
      if (cssText.length > maxSize) {
        const sizeKB = Math.round(cssText.length / 1024);
        const maxKB = Math.round(maxSize / 1024);
        setStatus({
          status: 'error',
          message: `Datei zu groß (${sizeKB} KB > ${maxKB} KB)`,
        });
        setPrefixedCss(null);
        return;
      }

      // Sanitize and prefix
      const prefixed = prefixCssSelectors(cssText, CSS_PREFIX);

      setStatus({ status: 'success', size: cssText.length, url: urlToLoad });
      setPrefixedCss(prefixed);
    } catch (error: unknown) {
      const err = error as { name?: string; message?: string };
      if (err.name === 'AbortError') {
        setStatus({ status: 'error', message: 'Timeout beim Laden' });
      } else if (error instanceof Error) {
        if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
          setStatus({ status: 'error', message: 'CORS-Fehler oder nicht erreichbar' });
        } else {
          setStatus({ status: 'error', message: error.message });
        }
      } else {
        setStatus({ status: 'error', message: 'Unbekannter Fehler' });
      }
      setPrefixedCss(null);
    }
  }, [maxSize, timeout]);

  // Debounced loading on URL change
  useEffect(() => {
    if (!url) {
      setStatus({ status: 'empty' });
      setPrefixedCss(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      loadStylesheet(url);
    }, debounce);

    return () => clearTimeout(timeoutId);
  }, [url, debounce, loadStylesheet]);

  const reset = useCallback(() => {
    setStatus({ status: 'empty' });
    setPrefixedCss(null);
  }, []);

  return {
    status,
    prefixedCss,
    reset,
  };
}
